// Theme Properties
export type FloorTheme = {
    name: string;
    description: string;
    colors: {
        primary: string;
        secondary: string;
        accent: string;
        background: string;
        text: string;
        card: string;
        border: string;
    };
    vibe: string;
    bgImage: string;
    moviePath: string;
    movieHlsPath?: string;
    // Visual Progression Properties
    radius: string;
    shadow: string;
    font: string;
    containerClass: string;
    avatarShape?: 'square' | 'rounded' | 'circle' | 'pentagon' | 'hexagon' | 'octagon' | 'diamond' | 'eye';
    features?: {
        current: string;
        next: string;
        demotion: string;
        details?: string;
    };
};

export const FLOOR_THEMES: Record<string, FloorTheme> = {
    // === LOW FLOORS (1-3: The Struggle - Brutalist/Sharp) ===
    'floor-1': {
        name: 'The Dungeon',
        description: '冷たく湿った監獄。ここが最底辺。',
        colors: {
            primary: '#a1a1aa', secondary: '#000000', accent: '#ffffff',
            background: '#000000', text: '#e4e4e7', card: '#18181b', border: '#3f3f46',
        },
        vibe: 'bg-black',
        bgImage: '/assets/images/Level1.jpg',
        moviePath: '/assets/movies/Floor1Movie.mp4',
        movieHlsPath: '/assets/movies/hls/Floor1Movie/master.m3u8',
        radius: 'rounded-none',
        shadow: 'shadow-none',
        font: 'font-mono',
        containerClass: 'bg-[radial-gradient(#27272a_1px,transparent_1px)] [background-size:16px_16px]',
        avatarShape: 'square',
        features: {
            current: '投稿制限：1日5回まで。',
            next: '回廊への昇格 (投稿制限7回/日)。',
            demotion: '追放 (アカウント停止)。',
            details: '・テキスト投稿のみ（1日5回）\n・スタンプ/画像/動画/投票/スポットライトは未解放'
        }
    },
    'floor-2': {
        name: 'The Corridor',
        description: '無機質な収容区画の廊下。',
        colors: {
            primary: '#64748b', secondary: '#020617', accent: '#64748b',
            background: '#020617', text: '#64748b', card: '#0f172a', border: '#1e293b',
        },
        vibe: 'bg-slate-950',
        bgImage: '/assets/images/Level2.jpg',
        moviePath: '/assets/movies/Floor2Movie.mp4',
        movieHlsPath: '/assets/movies/hls/Floor2Movie/master.m3u8',
        radius: 'rounded-sm',
        shadow: 'shadow-none',
        font: 'font-mono',
        containerClass: 'bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] [background-size:24px_24px]',
        avatarShape: 'square',
        features: {
            current: '投稿制限：1日7回まで。',
            next: '個室への昇格 (投稿制限10回/日)。',
            demotion: '監獄への降格 (投稿制限5回/日に減少)。',
            details: '・テキスト投稿のみ（1日7回）\n・スタンプ/画像/動画/投票/スポットライトは未解放'
        }
    },
    'floor-3': {
        name: 'The Dorm',
        description: '生活感のある狭い個室。',
        colors: {
            primary: '#4b5563', secondary: '#f3f4f6', accent: '#9ca3af',
            background: '#f9fafb', text: '#374151', card: '#ffffff', border: '#e5e7eb',
        },
        vibe: 'bg-gray-50',
        bgImage: '/assets/images/Level3.jpg',
        moviePath: '/assets/movies/Floor3Movie.mp4',
        movieHlsPath: '/assets/movies/hls/Floor3Movie/master.m3u8',
        radius: 'rounded',
        shadow: 'shadow-sm',
        font: 'font-sans',
        containerClass: 'bg-[repeating-linear-gradient(45deg,#f3f4f6_0px,#f3f4f6_10px,#ffffff_10px,#ffffff_20px)]', // Diagonal stripes
        avatarShape: 'square',
        features: {
            current: '投稿制限：1日10回まで。',
            next: '客室への昇格 (投稿制限15回/日) ＋ スタンプ解放。',
            demotion: '回廊への降格 (投稿制限7回/日に減少)。',
            details: '・テキスト投稿のみ（1日10回）\n・次階層でカスタムスタンプ（絵文字いいね）が解放'
        }
    },

    // === MIDDLE FLOORS (4-6: The Society - Modern/Rounded) ===
    'floor-4': {
        name: 'The Guest Room',
        description: '清潔で標準的なホテルの一室。',
        colors: {
            primary: '#be185d', secondary: '#fdf2f8', accent: '#db2777',
            background: '#fff1f2', text: '#831843', card: '#ffffff', border: '#fbcfe8',
        },
        vibe: 'bg-pink-50',
        bgImage: '/assets/images/Level4.jpg',
        moviePath: '/assets/movies/Floor4Movie.mp4',
        movieHlsPath: '/assets/movies/hls/Floor4Movie/master.m3u8',
        radius: 'rounded-lg',
        shadow: 'shadow',
        font: 'font-sans',
        containerClass: 'bg-[radial-gradient(#db2777_0.5px,transparent_0.5px)] [background-size:12px_12px]', // Polka dots
        avatarShape: 'rounded',
        features: {
            current: '投稿制限：1日15回まで。カスタムスタンプ。',
            next: 'テラスへの昇格 (投稿制限20回/日) ＋ 画像投稿機能追加。',
            demotion: '個室への降格 (投稿制限10回/日に減少)。',
            details: '・カスタムスタンプ（絵文字いいね）が利用可能\n・画像/動画/投票/スポットライトは未解放'
        }
    },
    'floor-5': {
        name: 'The Terrace',
        description: '陽光と緑溢れるバルコニー。',
        colors: {
            primary: '#059669', secondary: '#ecfdf5', accent: '#10b981',
            background: '#f0fdf4', text: '#064e3b', card: '#ffffff', border: '#a7f3d0',
        },
        vibe: 'bg-emerald-50',
        bgImage: '/assets/images/Level5.jpg',
        moviePath: '/assets/movies/Floor5Movie.mp4',
        movieHlsPath: '/assets/movies/hls/Floor5Movie/master.m3u8',
        radius: 'rounded-xl',
        shadow: 'shadow-md',
        font: 'font-sans',
        containerClass: 'bg-[radial-gradient(circle_at_center,rgba(16,185,129,0.1)_0%,transparent_70%)]', // Soft sun/green glow
        avatarShape: 'rounded',
        features: {
            current: '投稿制限：1日20回まで。画像投稿機能。',
            next: 'ラウンジへの昇格 (投稿制限25回/日)。投稿スポットライト機能の開放。',
            demotion: '客室への降格 (投稿制限15回/日に減少)。画像投稿機能はく奪。',
            details: '・画像投稿が解放（検閲あり）\n・動画/投票/スポットライトは未解放'
        }
    },
    'floor-6': {
        name: 'The Lounge',
        description: '夜景を望むシックなバーラウンジ。',
        colors: {
            primary: '#7e22ce', secondary: '#faf5ff', accent: '#a855f7',
            background: '#f3e8ff', text: '#581c87', card: '#ffffff', border: '#d8b4fe',
        },
        vibe: 'bg-purple-50',
        bgImage: '/assets/images/Level6.jpg',
        moviePath: '/assets/movies/Floor6Movie.mp4',
        movieHlsPath: '/assets/movies/hls/Floor6Movie/master.m3u8',
        radius: 'rounded-2xl',
        shadow: 'shadow-lg',
        font: 'font-sans',
        containerClass: 'bg-gradient-to-b from-purple-50/50 to-transparent',
        avatarShape: 'rounded',
        features: {
            current: '投稿制限：1日25回まで。投稿スポットライト機能。',
            next: 'スイートへの昇格 (投稿制限30回/日) ＋ 動画投稿機能。',
            demotion: 'テラスへの降格 (投稿制限20回/日に減少)。投稿スポットライト機能はく奪。',
            details: '・スポットライト解放：他者投稿を1件優先表示（切替可）\n・スポットライト中の投稿に反応が付くと +1\n・動画/投票は未解放'
        }
    },

    // === HIGH FLOORS (7-9: The Luxury - Glass/Serif) ===
    'floor-7': {
        name: 'The Suite',
        description: '洗練された都会の邸宅。',
        colors: {
            primary: '#b45309', secondary: '#fffbeb', accent: '#f59e0b',
            background: '#fffaf0', text: '#78350f', card: 'rgba(255, 255, 255, 0.9)', border: '#d97706',
        },
        vibe: 'bg-amber-50',
        bgImage: '/assets/images/Level7.jpg',
        moviePath: '/assets/movies/Floor7Movie.mp4',
        movieHlsPath: '/assets/movies/hls/Floor7Movie/master.m3u8',
        radius: 'rounded-3xl',
        shadow: 'shadow-xl',
        font: 'font-serif',
        containerClass: 'backdrop-blur-sm',
        avatarShape: 'circle',
        features: {
            current: '投稿制限：1日30回まで。動画投稿機能解放。',
            next: 'スパへの昇格 (投稿制限35回/日) ＋ 投票機能解放＋セルフスポットライト機能解放。',
            demotion: 'ラウンジへの降格 (投稿制限25回/日に減少)。動画投稿機能はく奪。',
            details: '・動画投稿が解放（検閲あり）\n・投票/セルフスポットライトは未解放'
        }
    },
    'floor-8': {
        name: 'The Spa',
        description: '水と光が織りなす癒しの空間。',
        colors: {
            primary: '#2563eb', secondary: '#eff6ff', accent: '#60a5fa',
            background: '#f0f9ff', text: '#1e3a8a', card: 'rgba(255, 255, 255, 0.9)', border: '#bfdbfe',
        },
        vibe: 'bg-blue-50',
        bgImage: '/assets/images/Level8.jpg',
        moviePath: '/assets/movies/Floor8Movie.mp4',
        movieHlsPath: '/assets/movies/hls/Floor8Movie/master.m3u8',
        radius: 'rounded-3xl',
        shadow: 'shadow-xl',
        font: 'font-serif',
        containerClass: 'backdrop-blur-sm',
        avatarShape: 'circle',
        features: {
            current: '投稿制限：1日35回まで。セルフスポットライト機能解放。',
            next: 'ペントハウスへの昇格 (投稿制限40回/日) ＋ 全投稿の優先表示＋投票機能。',
            demotion: 'スイートへの降格 (投稿制限30回/日に減少)。セルフスポットライト機能はく奪。',
            details: '・セルフスポットライト解放：自分の投稿を1件優先表示（反応で +1）\n・投票機能は次のフロアで解放'
        }
    },
    'floor-9': {
        name: 'The Penthouse',
        description: '天空のパノラマビュー。',
        colors: {
            primary: '#333333', secondary: '#ffffff', accent: '#999999',
            background: '#ffffff', text: '#111111', card: 'rgba(255, 255, 255, 0.95)', border: '#e5e5e5',
        },
        vibe: 'bg-white',
        bgImage: '/assets/images/Level9.jpg',
        moviePath: '/assets/movies/Floor9Movie.mp4',
        movieHlsPath: '/assets/movies/hls/Floor9Movie/master.m3u8',
        radius: 'rounded-3xl',
        shadow: 'shadow-2xl',
        font: 'font-serif',
        containerClass: 'backdrop-blur-md',
        avatarShape: 'circle',
        features: {
            current: '投稿制限：1日40回まで。タイムライン優先表示。投票機能解放。',
            next: '天界への到達 (投稿制限なし)。Admin、MaestroへのDM解放。',
            demotion: 'スパへの降格 (投稿制限35回/日に減少)。タイムライン優先表示権のはく奪。投票機能はく奪。',
            details: '・全体タイムラインで優先表示（スポットライトが優先）\n・投稿制限40回/日\n・投票機能（アンケート）が解放\n　- 1日3件まで作成可能\n　- 回答者は1人につき1ポイント、投稿者も1ポイント獲得\n　- 回答は1回のみで取り消し不可（投票時に確認モーダル）\n　- 公開範囲：フレンドのみ / 全体 / 指定フレンドのみ\n　- 質問文・選択肢は投稿と同じ検閲対象\n　- 選択肢は最大20個まで'
        }
    },
    'floor-10': {
        name: 'The Heaven',
        description: '雲の上の異次元空間。',
        colors: {
            primary: '#d4af37', secondary: '#141414', accent: '#f4e7c6',
            background: '#050505', text: '#f4e7c6', card: 'rgba(8, 8, 8, 0.94)', border: '#d4af37',
        },
        vibe: 'bg-zinc-950',
        bgImage: '/assets/images/Level10.jpg',
        moviePath: '/assets/movies/Floor10Movie.mp4',
        movieHlsPath: '/assets/movies/hls/Floor10Movie/master.m3u8',
        radius: 'rounded-full',
        shadow: 'shadow-2xl',
        font: 'font-serif',
        containerClass: 'backdrop-blur-xl bg-[radial-gradient(circle_at_top,rgba(212,175,55,0.16),transparent_52%),linear-gradient(to_bottom,rgba(5,5,5,0.86),rgba(5,5,5,0.96))]',
        avatarShape: 'circle',
        features: {
            current: '投稿制限なし。Maestroへの謁見の開放。',
            next: 'なし (最高階層)。',
            demotion: '謁見権限の剥奪。ペントハウスへの降格 (投稿制限40回/日に減少)。',
            details: '・投稿制限なし\n・Admin/Maestroへの謁見が解放'
        }
    },

    // === SPECIAL ROLES ===
    'admin': {
        name: 'The Control Room',
        description: 'すべてを見通す部屋。',
        colors: {
            primary: '#d4af37', secondary: '#141414', accent: '#f4e7c6',
            background: '#050505', text: '#f4e7c6', card: 'rgba(8, 8, 8, 0.95)', border: '#d4af37',
        },
        vibe: 'bg-zinc-950',
        bgImage: '/assets/images/MaestroLevel.jpg',
        moviePath: '/assets/movies/MaestroFloorMovie.mp4',
        movieHlsPath: '/assets/movies/hls/MaestroFloorMovie/master.m3u8',
        radius: 'rounded-none',
        shadow: 'shadow-2xl',
        font: 'font-serif',
        containerClass: 'bg-[radial-gradient(circle_at_top,rgba(212,175,55,0.18),transparent_48%),radial-gradient(#d4af37_0.8px,transparent_0.8px)] [background-size:100%_100%,28px_28px]',
        avatarShape: 'eye',
        features: {
            current: 'システム全域の管理権限。ユーザーの監視・制裁執行。',
            next: 'なし。',
            demotion: 'なし。',
            details: '・ユーザー/投稿/AI/問い合わせを包括的に管理\n・全フロアへのアクセスと制裁権限'
        }
    },
    'manager': {
        name: 'The Manager Office',
        description: '管理者の執務室。',
        colors: {
            primary: '#18181b', secondary: '#f4f4f5', accent: '#52525b', // Monotone
            background: '#ffffff', text: '#09090b', card: '#ffffff', border: '#27272a',
        },
        vibe: 'bg-zinc-50',
        bgImage: '/assets/images/ManagerLevel.jpg',
        moviePath: '/assets/movies/ManagerFloorMovie.mp4',
        movieHlsPath: '/assets/movies/hls/ManagerFloorMovie/master.m3u8',
        radius: 'rounded-sm',
        shadow: 'shadow-none',
        font: 'font-mono',
        containerClass: 'bg-[radial-gradient(#e4e4e7_1px,transparent_1px)] [background-size:16px_16px]',
        avatarShape: 'pentagon',
        features: {
            current: '全機能利用可能。',
            next: 'なし。',
            demotion: 'なし。',
            details: '・ユーザーサポートと管理機能\n・問い合わせ/上奏の対応が可能'
        }
    }
};

export const getTheme = (role: string, floor: number) => {
    if (role === 'Admin') return FLOOR_THEMES['admin'];
    if (role === 'Maestro') return FLOOR_THEMES['floor-10'];
    if (role === 'Manager') return FLOOR_THEMES['manager'];
    const key = `floor-${Math.min(Math.max(floor, 1), 10)}`;
    return FLOOR_THEMES[key] || FLOOR_THEMES['floor-3'];
};
